<script setup lang="ts">
import { getSanitizedNavigationQueryAndHash } from '@pkg/utilities/urls/sanitizeNavigationQuery';

/**
 * Shared header/footer Lottie logo for web + business.
 * Host apps register this via Nuxt `components.dirs` so PartialHeader /
 * MobileNav / PartialFooter can `resolveComponent('TGTGLogo')`.
 */
const logoSrc = '/lottie/tgtg-logo-unfold-green.040424.lottie';

const props = withDefaults(
  defineProps<{
    theme?: 'light' | 'dark';
    fetchpriority?: 'high';
    headerLogo?: boolean;
  }>(),
  {
    theme: 'dark',
    fetchpriority: undefined,
    headerLogo: false,
  },
);

const localePath = useLocalePath();
const route = useRoute();
const runtimeConfig = useRuntimeConfig();

// Only pass props that differ from the player's defaults. `controls`, `loop` and
// `subframe` default to false. Binding `false` writes the *string* attribute
// (`controls="false"`) whenever Vue renders before the custom element is upgraded,
// and the player reads any present attribute as true, which shows its toolbar.
const lottieProps = {
  autoplay: true,
};

const logoClass = computed(() => {
  if (props.theme === 'light') {
    return 'logo logo--inverted';
  }
  if (props.headerLogo && props.theme === 'dark') {
    return 'logo logo--black';
  }
  return 'logo';
});

const homeTo = computed(() => {
  const { query, hash } = getSanitizedNavigationQueryAndHash(
    route.query,
    route.hash,
    (runtimeConfig.public as { paramsAllowList?: string }).paramsAllowList,
  );
  const path = localePath('/');
  return {
    path,
    ...(Object.keys(query).length > 0 ? { query } : {}),
    ...(hash ? { hash } : {}),
  };
});

if (props.fetchpriority === 'high') {
  useHead({
    link: [{
      rel: 'preload',
      href: logoSrc,
      as: 'fetch',
      fetchpriority: 'high',
      crossorigin: 'anonymous',
    }],
  });
}
</script>

<template>
  <NuxtLink
    :to="homeTo"
    class="logo-link amp-auto-track"
    aria-label="Go to homepage"
  >
    <dotlottie-player
      :src="logoSrc"
      :class="logoClass"
      v-bind="lottieProps"
    />
  </NuxtLink>
</template>

<style scoped>
.logo-link {
  position: relative;
  width: 100px;
  min-width: 100%;
  max-width: 100%;
  height: 100%;
  display: flex;
  color: inherit;
  text-decoration: none;
}

.logo {
  /* Lottie file is adding some spacing around this, so we're cutting it off */
  position: absolute;
  inset: 0;
  inset-inline: -30%;
  width: auto;
  transition: filter 0.3s ease;
}

.logo--inverted {
  filter: brightness(0) invert(1);
}

.logo--black {
  filter: brightness(0);
}
</style>
